import {
  Table,
  TextInput,
  Group,
  Button,
  Pagination,
  Text,
  LoadingOverlay,
  Center,
  Stack,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useState, useMemo } from "react";

export interface AdminColumn<T> {
  key: keyof T | string;
  label: string;
  width?: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

interface AdminDataTableProps<T> {
  data: T[];
  columns: AdminColumn<T>[];
  isLoading?: boolean;
  onAdd?: () => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onToggleActive?: (row: T) => void;
  onRowClick?: (row: T) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  pageSize?: number;
  getId?: (row: T) => string;
  getIsActive?: (row: T) => boolean;
  filterSlot?: React.ReactNode;
}

export default function AdminDataTable<T extends Record<string, any>>({
  data,
  columns,
  isLoading = false,
  onAdd,
  onEdit,
  onDelete,
  onToggleActive,
  onRowClick,
  searchPlaceholder = "Search...",
  emptyMessage = "No data available",
  pageSize = 20,
  getId,
  getIsActive,
  filterSlot,
}: AdminDataTableProps<T>) {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!search) return data;

    return data.filter((row) => {
      return Object.values(row).some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(search.toLowerCase());
      });
    });
  }, [data, search]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [filteredData, sortBy, sortOrder]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  if (isLoading) {
    return (
      <div style={{ position: "relative", minHeight: 400 }}>
        <LoadingOverlay visible />
      </div>
    );
  }

  return (
    <Stack gap="md">
      {/* Search, Filters, and Add Button */}
      <Group justify="space-between">
        <Group gap="md">
          <TextInput
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: 300 }}
          />
          {filterSlot}
        </Group>
        {onAdd && (
          <Button leftSection={<IconPlus size={16} />} onClick={onAdd}>
            Add New
          </Button>
        )}
      </Group>

      {/* Results count */}
      <Text size="sm" c="dimmed">
        Showing {paginatedData.length} of {sortedData.length} results
        {search && ` (filtered from ${data.length} total)`}
      </Text>

      {/* Table */}
      {paginatedData.length === 0 ? (
        <Center py={60}>
          <Text c="dimmed">{emptyMessage}</Text>
        </Center>
      ) : (
        <>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                {columns.map((column) => (
                  <Table.Th
                    key={String(column.key)}
                    style={{
                      width: column.width,
                      cursor: column.sortable ? "pointer" : "default",
                      textAlign:
                        column.key === "review_actions" ? "center" : "left",
                    }}
                    onClick={() =>
                      column.sortable && handleSort(String(column.key))
                    }
                  >
                    <Group
                      gap={4}
                      justify={
                        column.key === "review_actions" ? "center" : undefined
                      }
                    >
                      <Text size="sm" fw={600}>
                        {column.label}
                      </Text>
                      {column.sortable && sortBy === column.key && (
                        <Text size="xs" c="dimmed">
                          {sortOrder === "asc" ? "↑" : "↓"}
                        </Text>
                      )}
                    </Group>
                  </Table.Th>
                ))}
                {(onEdit || onDelete || onToggleActive) && (
                  <Table.Th style={{ width: "106px", textAlign: "center" }}>
                    Actions
                  </Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedData.map((row, index) => {
                const isActive = getIsActive ? getIsActive(row) : true;
                return (
                  <Table.Tr
                    key={getId ? getId(row) : index}
                    style={{
                      opacity: isActive ? 1 : 0.5,
                      cursor: onRowClick ? "pointer" : undefined,
                    }}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <Table.Td key={String(column.key)}>
                        {column.render
                          ? column.render(row)
                          : String(row[column.key] ?? "")}
                      </Table.Td>
                    ))}
                    {(onEdit || onDelete || onToggleActive) && (
                      <Table.Td>
                        <Group gap="sm">
                          {onEdit && (
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                size="input-sm"
                                onClick={() => onEdit(row)}
                              >
                                <IconEdit size={20} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {onToggleActive && (
                            <Tooltip
                              label={isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={isActive ? "red" : "green"}
                                size="input-sm"
                                onClick={() => onToggleActive(row)}
                              >
                                {isActive ? (
                                  <IconX size={20} />
                                ) : (
                                  <IconCheck size={20} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="input-sm"
                                onClick={() => onDelete(row)}
                              >
                                <IconTrash size={20} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Center mt="md">
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </Center>
          )}
        </>
      )}
    </Stack>
  );
}
